// WP-T-2 (UX v1.6 §10.9) — Titled Card: the content container for every
// section under a Page Header, every dashboard tile and every list row once
// a Data Table collapses on a phone. Surface fill, radius.card, elevation.rest,
// card padding; the title row is type.h3 uppercase in ink.muted with an
// optional right-aligned tertiary action and a completeness slot ("3 ✓",
// "not yet"). Hover lifts to elevation.raised; focus takes the §10.6 ring on
// the card edge. Every value comes from the role custom properties in
// theme/tokens.css (hand-written rules above the generated region).
//
// Presentation only (Track U non-break rule 1): the card decides how a section
// LOOKS, never what it contains or does — callers pass their existing controls.
import type { HTMLAttributes, ReactNode } from "react";

export type TitledCardHeadingLevel = 2 | 3 | 4;
export type TitledCardTone = "default" | "hero";
export type TitledCardElement = "section" | "article" | "div" | "li" | "aside";

export interface TitledCardProps extends Omit<HTMLAttributes<HTMLElement>, "title"> {
  /** Title row text (rendered uppercase by CSS, so pass sentence case). */
  title?: ReactNode;
  /** `id` placed on the heading; the card gets `aria-labelledby` to match. */
  titleId?: string;
  /** Semantic heading level — the page's h1 is the Page Header, so sections default to h2. */
  headingLevel?: TitledCardHeadingLevel;
  /** Optional right-aligned action (a tertiary button or link the screen already has). */
  action?: ReactNode;
  /** Completeness read-out in the title row (§10.9: `3 ✓`, `not yet`). */
  completeness?: ReactNode;
  /** `hero` tints the card with brand.fill (O-02 hero tile). */
  tone?: TitledCardTone;
  /** Element to render — `li` when the card is a collapsed table row. */
  as?: TitledCardElement;
  children?: ReactNode;
}

const HEADINGS: Record<TitledCardHeadingLevel, "h2" | "h3" | "h4"> = { 2: "h2", 3: "h3", 4: "h4" };

export function TitledCard({
  title,
  titleId,
  headingLevel = 2,
  action,
  completeness,
  tone = "default",
  as = "section",
  className,
  children,
  ...rest
}: TitledCardProps) {
  const Tag = as;
  const Heading = HEADINGS[headingLevel];
  const classes = ["titled-card", tone === "hero" ? "titled-card--hero" : "", className ?? ""]
    .filter(Boolean)
    .join(" ");
  const hasHead = title !== undefined || action !== undefined || completeness !== undefined;

  return (
    <Tag
      className={classes}
      aria-labelledby={title !== undefined && titleId ? titleId : undefined}
      {...rest}
    >
      {hasHead ? (
        <div className="titled-card__head">
          {title !== undefined ? (
            <Heading id={titleId} className="titled-card__title">
              {title}
            </Heading>
          ) : null}
          {completeness !== undefined ? (
            <span className="titled-card__completeness">{completeness}</span>
          ) : null}
          {action !== undefined ? <div className="titled-card__action">{action}</div> : null}
        </div>
      ) : null}
      <div className="titled-card__body">{children}</div>
    </Tag>
  );
}

/**
 * §10.9 — cards sit in a 16 px gap grid on the surface.sunken canvas: two
 * columns at ≥ 1024 px, one below. A child with `card-grid__span` takes the
 * full row (e.g. the O-05 media section).
 */
export function CardGrid({ className, children, ...rest }: HTMLAttributes<HTMLDivElement>) {
  const classes = ["card-grid", className ?? ""].filter(Boolean).join(" ");
  return (
    <div className={classes} {...rest}>
      {children}
    </div>
  );
}
